import React, { useEffect, useState, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDebouncedValue } from '@mantine/hooks';
import {
  Button,
  RadioGroup,
  Radio,
  Group,
  Text,
  Textarea,
  Box,
  Title,
  Divider,
  TextInput,
  Select,
  Avatar,
  ActionIcon,
  Checkbox,
  CheckboxGroup,
  SimpleGrid,
  MultiSelect,
    Stack,
    Grid,
    Container,
    Code,
    Loader,
  Center
} from '@mantine/core';
import { User, Briefcase, Phone, Trash } from 'tabler-icons-react';
import { DatePicker } from '@mantine/dates';
import { useForm, formList } from '@mantine/form';
import externalApi from '../api';
import endpoints from '../api/endpoints';
import { FormList } from '@mantine/form/lib/form-list/form-list';
import { showNotification } from '@mantine/notifications';
import {GraphUser, UDFPickListResults, TicketResponse, DeviceResponse, FunctionalCentre, SelectOption} from '../types'

type AllGraphUsers = {
  '@odata.context': string;
  '@odata.nextLink': string;
  value: GraphUser[];
};

type DeviceRequestTicket = {
  requestType: string;
  dateRequired: string;
  deviceFor: string;
  deviceForStaffMember: GraphUser | undefined;
  deviceList: FormList<DeviceResponse>;
  functionalCentreNumber: string;
  installationSite: string;
  installationDepartment: string;
  installationLocation: string;
  installationDeviceName: string;
  comments: string;
};

interface ItemProps extends React.ComponentPropsWithoutRef<'div'> {
  image: string;
  label: string;
  value: string;
  description: string;
  initials: string;
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ image, label, description, initials, ...others }: ItemProps, ref) => (
    <div ref={ref} {...others}>
      <Group noWrap>
        <Avatar src={image}>{initials}</Avatar>
        <div>
          <Text size="sm">{label}</Text>
          <Text size="xs" color="dimmed">
            {description}
          </Text>
        </div>
      </Group>
    </div>
  )
);

const DeviceRequest = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<GraphUser>();
  const [manager, setManager] = useState<GraphUser>();
  const [onBehalfOf, setOnBehalfOf] = useState(false);
  const [installSites, setInstallSites] = useState<string[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [functionalCentres, setFunctionalCentres] = useState<SelectOption[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<SelectOption[]>([]);
    const [loadingState, setLoadingState] = useState(false);
    const [isDataLoaded, setIsDataLoaded] = useState(true);

  const [userSearchInput, setUserSearchInput] = useState('');
  const [debouncedUserSearchInput] = useDebouncedValue(userSearchInput, 200);
  const [userSelectData, setUserSelectData] = useState<ItemProps[]>([]);
  const [staffMember, setStaffMember] = useState<GraphUser>();

  const ticket: DeviceRequestTicket = {
    requestType: '',
    dateRequired: '',
    deviceFor: '',
    deviceForStaffMember: staffMember,
    deviceList: formList([]),
    functionalCentreNumber: '',
    installationSite: '',
    installationDepartment: '',
    installationLocation: '',
    installationDeviceName: '',
    comments: '',
  };

  const form = useForm<DeviceRequestTicket>({
    initialValues: ticket,
    validate: {},
  });

  async function submitForm() {
    let response: TicketResponse;
    setLoadingState(true);
    if (onBehalfOf) {
      response = await externalApi
        .post(endpoints.submitDeviceRequestTicket.url, {
          json: { ...form.values, requester: manager, submittedBy: user },
        })
        .json();
    } else {
      response = await externalApi
        .post(endpoints.submitDeviceRequestTicket.url, { json: { ...form.values, requester: user } })
        .json();
    }

    if (response.success === true) {
      navigate(`/request-submitted/${response.ticketId}`);
    } else {
      console.log(response);
      setLoadingState(false);
      showNotification({
        title: 'Error Submitting Form',
        color: 'red',
        message: response.response,
      });
    }
  }

  async function loadUser() {
    const me: GraphUser = await externalApi.get(endpoints.graphMe.url).json();
    setUser(me);
  }

  async function loadManager(userPrincipal: string) {
    const graphManager: GraphUser = await externalApi
      .get(
        `${endpoints.graphProfilePhoto.url}/${userPrincipal}?$select=businessPhones,displayName,givenName,jobTitle,mail,mobilePhone,officeLocation,preferredLanguage,surname,department,userPrincipalName,id`
      )
      .json();

    setManager(graphManager);
  }

  async function getUsersFromGraph(displayNameSearch: string) {
    const users: AllGraphUsers = await externalApi
      .get(`${endpoints.graphUsers.url}&$search="displayName:${displayNameSearch}"`, {
        headers: { ConsistencyLevel: 'eventual' },
      })
      .json();

    let searchUsers: ItemProps[] = [];
    users.value.forEach(async (searchUser) => {
      if (searchUser.id !== null) {
        let profilePhotoUrl: string = ''; //await getProfilePhoto(searchUser.id);
        let initials: string =
          searchUser.givenName?.length > 0 ? `${searchUser.givenName?.at(0)}${searchUser.surname?.at(0)}` : '';

        searchUsers.push({
          image: profilePhotoUrl,
          description: searchUser.jobTitle,
          value: searchUser.id,
          label: searchUser.displayName,
          initials: initials,
        });
      }
    });

    setUserSelectData(searchUsers);
  }

  async function loadStaffMember(userPrincipal: string) {
    const graphUser: GraphUser = await externalApi
      .get(
        `${endpoints.graphProfilePhoto.url}/${userPrincipal}?$select=businessPhones,displayName,givenName,jobTitle,mail,mobilePhone,officeLocation,preferredLanguage,surname,department,userPrincipalName,id`
      )
      .json();

    setStaffMember(graphUser);
    form.setFieldValue('deviceForStaffMember', graphUser);
  }

  async function loadDepartments() {
    const departmentsResult: UDFPickListResults = await externalApi.get(endpoints.getSdPlusDepartments.url).json();
    setDepartments(departmentsResult.values);
  }

  async function loadDevices() {
    const devicesResult: DeviceResponse[] = await externalApi.get(endpoints.getDeviceTypes.url).json();

    const devices = devicesResult.map((a: DeviceResponse) => {
      return {
        label: a.name,
        value: a.subCategoryId,
      };
    });

    setDeviceTypes(devices);
  }

  async function loadInstallSites() {
    const sites: UDFPickListResults = await externalApi.get(endpoints.getDesignatedSites.url).json();
    setInstallSites(sites.values);
  }

  async function loadFunctionalCentres() {
    const functionalCentres: FunctionalCentre[] = await externalApi.get(endpoints.getFunctionalCentreList.url).json();

    const fcs = functionalCentres.map((a: FunctionalCentre) => {
      return {
        label: a.functionalCentreName + ' (' + a.functionalCentreNumber + ')',
        value: a.functionalCentreNumber,
        key: a.functionalCentreReference,
      };
    });

    setFunctionalCentres(fcs);
  }

  const deviceChecks = deviceTypes.filter((device) => device.label != 'Multiple Devices').map((filteredDevice, index) => (

      <Checkbox
          key={index}
          value={filteredDevice.value}
          label={filteredDevice.label}
              onChange={(e) => {
                  if (e.currentTarget.checked)
                      form.addListItem('deviceList', { subCategoryId: filteredDevice.value, name: filteredDevice.label });
                  else form.removeListItem('deviceList', index);
              }}
          />
  ));

    async function loadAllData() {
        loadUser();
        loadDepartments();
        loadFunctionalCentres();
        loadDevices();
        loadInstallSites();
        setIsDataLoaded(true);
    }

  useEffect(() => {
    loadAllData();
    if (debouncedUserSearchInput !== '') getUsersFromGraph(debouncedUserSearchInput);
  }, [debouncedUserSearchInput]);

  return (
    <>
      <Title my="sm" order={1}>
        Device Request
      </Title>

      <form onSubmit={form.onSubmit(() => submitForm())}>
        <Box>
          <Checkbox
            color="blue"
            label="Submitting On Behalf of Manager"
            checked={onBehalfOf}
            onChange={(event) => setOnBehalfOf(event.currentTarget.checked)}
          />

          {!onBehalfOf && (
            <Box>
              <Divider my="sm" label="Requester" labelProps={{ size: 'lg' }} />

              <Stack>
                <Group>
                  <User />
                  <Text>{user?.displayName}</Text>
                </Group>
                <Group>
                  <Briefcase />
                  <Text>{user?.jobTitle},</Text>
                  <Text>{user?.department}</Text>
                </Group>
                <Group>
                  <Phone />
                  <Text>{user?.businessPhones}</Text>
                </Group>
              </Stack>
            </Box>
          )}
          {onBehalfOf && (
            <Box>
              <Divider my="sm" label="Manager" labelProps={{ size: 'lg' }} />
              <Stack>
                <Select
                  searchable
                  label="Select Manager"
                  nothingFound="No Manager Found"
                  itemComponent={SelectItem}
                  onSearchChange={(input: string) => setUserSearchInput(input)}
                  onChange={(value: string) => loadManager(value)}
                  data={userSelectData}
                />
                <Group>
                  <User />
                  <Text>{manager?.displayName}</Text>
                </Group>
                <Group>
                  <Briefcase />
                  <Text>{manager?.jobTitle},</Text>
                  <Text>{manager?.department}</Text>
                </Group>
                <Group>
                  <Phone />
                  <Text>{manager?.businessPhones}</Text>
                </Group>
                <Text>Submitted by: {user?.displayName}</Text>
              </Stack>
            </Box>
          )}
        </Box>
        <Divider my="sm" label="Request Details" labelProps={{ size: 'lg' }} />
        <RadioGroup
          {...form.getInputProps('requestType')}                 
          required
          label="Device Request Type"
          color="blue"
          size="sm"
        >
          <Radio value="New Addition" label="New Addition" />
          <Radio value="Replace Existing" label="Replace Existing" />
        </RadioGroup>

        {form.values['requestType'] && (
          <Box>
            <DatePicker
              {...form.getInputProps('dateRequired')}
              required
              my="md"
              label="When do you need this device by?"
              firstDayOfWeek="sunday"
            />

            <RadioGroup
              {...form.getInputProps('deviceFor')}
              label="Who is this device for?"
              required
              color="blue"
              size="sm"
            >
              <Radio value="Shared" label="Shared Device (nursing station, meeting room, etc.)" />
              <Radio value="Staff" label="Staff Device" />
              <Radio value="Manager" label="Manager Device" />
              <Radio value="Director" label="Director and Above Device" />
            </RadioGroup>

            {(form.values['deviceFor'] == 'Staff' ||
              form.values['deviceFor'] == 'Manager' ||
              form.values['deviceFor'] == 'Director') && (
              <Select
                searchable
                my="sm"
                label="Select User"
                nothingFound="No User Found"
                itemComponent={SelectItem}
                onSearchChange={(input: string) => setUserSearchInput(input)}
                onChange={(value: string) => loadStaffMember(value)}
                data={userSelectData}
              />
            )}

            <Divider my="sm" label="Device Details" labelProps={{ size: 'lg' }} />

                      {/*<CheckboxGroup label="Select devices">*/}
                      <Grid  >
                          <Grid.Col xl={6} lg={7}  sm={9}>
                      <Container size="md">
                <SimpleGrid
                cols={4}
                spacing="md"
                breakpoints={[
                    { maxWidth: 992, cols: 3, spacing: 'md' },
                    { maxWidth: 720, cols: 2, spacing: 'md' },
                    { maxWidth: 450, cols: 1, spacing: 'md' },
                ]}
                      >
                          {deviceChecks}
                              
                          </SimpleGrid>
                              </Container>
                              </Grid.Col>
                      </Grid>

            {/* </CheckboxGroup>*/}

            <Divider my="sm" label="Location Information" labelProps={{ size: 'lg' }} />
            {!isDataLoaded && (<Center><Loader variant="dots" my="xl" size="lg" /></Center>)}
            { isDataLoaded && (<Box>
            <Select
              my="md"
              size="sm"
              searchable
              clearable
              {...form.getInputProps('functionalCentreNumber')}
              label="Functional Centre"
              placeholder="Select Functional Centre"
              nothingFound="No Functional Centre Found"
              data={functionalCentres}
            />

            <Select
              my="md"
              size="sm"
              searchable
              clearable
              {...form.getInputProps('installationSite')}
              label="Install Site"
              placeholder="Select Install Site"
              nothingFound="No Site Found"
              data={installSites}
            />

            <Select
              my="md"
              size="sm"
              searchable
              clearable
              {...form.getInputProps('installationDepartment')}
              label="Install Department"
              placeholder="Select Department"
              nothingFound="No Department Found"
              data={departments}
                      />
                      </Box>)}
            <Textarea
                label="Install Location"
                {...form.getInputProps('installationLocation')}
                placeholder="Describe install location in specific detail"
            />
            <Textarea
              label="Install Device Name"
              my="sm"
              {...form.getInputProps('installationDeviceName')}
              placeholder="Please include the device(s) name to be replaced or to which the peripheral(s) should be installed on"
            />
            

            <Textarea label="Comments" {...form.getInputProps('comments')} placeholder="" />
          </Box>
        )}
        {form.values['requestType'] !== '' && (
          <Button my="sm" type="submit" loading={loadingState}>
            Submit Ticket
          </Button>
        )}
      </form>
    </>
  );
};

export default DeviceRequest;
